import { type FC, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { FormattedPrice } from 'dibs-buyer-price/exports/FormattedPrice';

import { type ProductPrice_item$key } from './__generated__/ProductPrice_item.graphql';

import dibsCss from 'dibs-css';

const TYPES = {
    SALE: 'WEEKLY_SALE',
    NET: 'NET',
    RETAIL: 'RETAIL',
    SOLD: 'SOLD',
    HOLD: 'HOLD',
    UNAVAILABLE: 'UNAVAILABLE',
    PUR: 'PUR',
    SOLD_PRICE: 'SOLD_PRICE',
} as const;

const TEXT_PRICE_TYPES = new Set<string>([TYPES.SOLD, TYPES.HOLD, TYPES.UNAVAILABLE, TYPES.PUR]);

const messages = {
    saleLabel: <FormattedMessage id="dc.buyProductTile.salePrice" defaultMessage="Sale Price" />,
    netLabel: <FormattedMessage id="dc.buyProductTile.netPrice" defaultMessage="Net Price" />,
    retailLabel: <FormattedMessage id="dc.buyProductTile.listPrice" defaultMessage="List Price" />,
    soldPriceLabel: (
        <FormattedMessage id="dc.buyProductTile.similarSoldPrice" defaultMessage="Sold Price" />
    ),
    priceVariabilityLabel: (
        <FormattedMessage defaultMessage="From " id="dc.buyerProductTile.priceVariability" />
    ),
    textPercentageOff: (percentageOff: number | string): ReactElement => (
        <FormattedMessage
            id="dc.buyProductTile.percentageOff"
            defaultMessage={`{percentageOff}% Off`}
            values={{ percentageOff }}
        />
    ),
    textPriceValue: (textPriceType: string): ReactElement => (
        <FormattedMessage
            values={{ textPriceType }}
            id="dc.searchProductTile.textPriceValue"
            defaultMessage={`{textPriceType, select,
            SOLD {Sold}
            UNAVAILABLE {Unavailable}
            PUR {Price Upon Request}
            HOLD {On Hold}
            other {Sold}
        }`}
        />
    ),
};

const itemFragment = graphql`
    fragment ProductPrice_item on Item
    @argumentDefinitions(
        isTrade: { type: "Boolean", defaultValue: false }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
        priceBookName: { type: "String" }
        showPrice: { type: "Boolean", defaultValue: true }
    ) {
        displayPrice(page: $pageDisplayEnum, isTrade: $isTrade, priceBookName: $priceBookName)
            @include(if: $showPrice) {
            convertedAmountList {
                amount
                currency
            }
            quantityDisplay
            percentageOff
            textType
            amountType
            showPriceLabel
            showPriceVariability
        }
    }
`;

type Props = {
    currency: string;
    item: ProductPrice_item$key | null | undefined;
    hideRecentSoldPrice?: boolean;
};

export const ProductPrice: FC<Props> = ({
    item: itemRef,
    currency,
    hideRecentSoldPrice = false,
}) => {
    const item = useFragment(itemFragment, itemRef);

    const {
        textType,
        amountType,
        convertedAmountList,
        quantityDisplay,
        percentageOff,
        showPriceLabel,
        showPriceVariability,
    } = item?.displayPrice?.[0] || {};

    const amountByCurrency =
        (convertedAmountList || []).find(convertedAmount => convertedAmount?.currency === currency)
            ?.amount || 0;
    const price = amountByCurrency < 1 ? amountByCurrency : Math.floor(amountByCurrency);

    if (!price || !amountType) {
        return null;
    }
    const quantityDisplayCopy = typeof quantityDisplay === 'string' ? ` ${quantityDisplay}` : null;

    if (typeof textType === 'string' && TEXT_PRICE_TYPES.has(textType)) {
        const showPrice = !hideRecentSoldPrice && price > 0 && textType !== TYPES.PUR;
        return (
            <p
                className={classnames(dibsCss.textBodySmallNormal, dibsCss.mtXxsmall)}
                data-tn={`price-${textType}`}
            >
                <span className={classnames(dibsCss.textAlertsecondary, dibsCss.mrXxsmall)}>
                    {messages.textPriceValue(textType)}
                </span>
                {showPrice && (
                    <span data-tn="price-retail" className={dibsCss.textBodySmallHeavy}>
                        <span data-tn="price">
                            <FormattedPrice value={price} currency={currency} />
                            {quantityDisplayCopy}
                        </span>
                    </span>
                )}
            </p>
        );
    }

    const PriceComponent = (
        <>
            {showPriceVariability ? messages.priceVariabilityLabel : null}
            <span
                data-tn="price"
                className={classnames(dibsCss.textBodySmallHeavy, dibsCss.mrXxsmall)}
            >
                <FormattedPrice value={price} currency={currency} />
            </span>
        </>
    );

    if (amountType === TYPES.SALE) {
        return (
            <p
                data-tn="price-sale"
                className={classnames(dibsCss.textBodySmallNormal, dibsCss.mtXxsmall)}
            >
                {PriceComponent}
                {messages.saleLabel}
                {quantityDisplayCopy}
                {percentageOff ? (
                    <span
                        data-tn="discount-amount"
                        className={classnames(
                            dibsCss.textAlertsecondary,
                            dibsCss.mlXsmall,
                            dibsCss.plXsmall,
                            dibsCss.borderLSolid,
                            dibsCss.borderL,
                            dibsCss.borderLDolphin
                        )}
                    >
                        {messages.textPercentageOff(percentageOff)}
                    </span>
                ) : null}
            </p>
        );
    }

    if (amountType === TYPES.NET) {
        return (
            <p
                data-tn="price-net"
                className={classnames(dibsCss.textBodySmallNormal, dibsCss.mtXxsmall)}
            >
                {PriceComponent}
                {messages.netLabel}
                {quantityDisplayCopy}
                {percentageOff ? (
                    <span
                        data-tn="discount-amount"
                        className={classnames(
                            dibsCss.textTradeprimary,
                            dibsCss.mlXsmall,
                            dibsCss.plXsmall,
                            dibsCss.borderLSolid,
                            dibsCss.borderL,
                            dibsCss.borderLDolphin
                        )}
                    >
                        {messages.textPercentageOff(percentageOff)}
                    </span>
                ) : null}
            </p>
        );
    }

    if (amountType === TYPES.RETAIL) {
        if (textType === TYPES.SOLD_PRICE) {
            return (
                <p
                    data-tn="price-similar-sold"
                    className={classnames(dibsCss.textBodySmallNormal, dibsCss.mtXxsmall)}
                >
                    {PriceComponent}
                    {messages.soldPriceLabel}
                    {quantityDisplayCopy}
                </p>
            );
        }
        return (
            <p
                data-tn="price-retail"
                className={classnames(dibsCss.textBodySmallNormal, dibsCss.mtXxsmall)}
            >
                {PriceComponent}
                {showPriceLabel ? messages.retailLabel : null}
                {quantityDisplayCopy}
            </p>
        );
    }

    return null;
};

// {priceVariabilityLabel}{priceIntl}{priceLabel}{quantityDisplay}{discount} eg: "From $100  Sale Price / set | 10% Off"
// priceVariabilityLabel: Text `From `, no text
// price: itemPrice + currency
// praceLabel: Sale Price, Net Price, List Price, Sold Price (used in similarSold page)
// quantityDisplay: `/set` or `/item`, no text
// discount: percentageOff

// {unavailableStatus}{priceIntl} eg. "Sold $100"
// unavailableStatus: Sold, On Hold, Unavailable, Price Upon Request
// price: itemPrice + currency
